import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  apiConfigDeployments,
  createDeployment,
  deleteDeployment,
  updateDeployment,
} from "@/api/resources/deployments/api";
import { deploymentsQueries, deploymentsQueryKeys } from "@/api/resources/deployments/queries";
import { selectorCurrentDeployments, selectorFilteredDeployments } from "@/api/resources/deployments/selectors";
import { deploymentsToWorkspacesQueries } from "@/api/resources/deploymentsToWorkspaces/queries";
import i18n from "@/providers/i18n";
import { spawnCreateMutationHook, spawnDeleteMutationHook, spawnUpdateMutationHook } from "@/utils/api";
import { makeQueryFilterIn, type QueryFilter } from "@/utils/filter";

const entity = i18n.t("deployment");

export const useDeployments = () => {
  return useQuery(deploymentsQueries.listAll());
};

export const useDeployment = (deployment_id?: string) => {
  return useQuery(deploymentsQueries.byId(deployment_id));
};

export const useCurrentDeployment = () => {
  const { deployment_id } = useParams({ strict: false });
  return useQuery(deploymentsQueries.byId(deployment_id));
};

export const useFilteredDeployments = (workspace_id?: string) => {
  const { workspace_id: _workspace_id } = useParams({ strict: false });
  const { data: deploymentsToWorkspaces } = useQuery(deploymentsToWorkspacesQueries.listAll());
  return useQuery({
    ...deploymentsQueries.listAll(),
    select: (deployments) =>
      selectorFilteredDeployments({
        currentWorkspaceId: workspace_id ?? _workspace_id,
        deployments,
        deploymentsToWorkspaces: deploymentsToWorkspaces ?? [],
      }),
  });
};

export const useCurrentDeployments = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery({
    ...deploymentsQueries.listAll(),
    select: (deployments) => selectorCurrentDeployments({ currentWorkspaceId: workspace_id, deployments }),
  });
};

export const useDeploymentsQueryFilters = () => {
  const { data: currentDeployment } = useCurrentDeployment();
  const { data: currentDeployments } = useCurrentDeployments();
  const { data: filteredDeployments } = useFilteredDeployments();

  return {
    currentDeployment: currentDeployment ? (["deployment_id", "eq", currentDeployment.id] as QueryFilter) : undefined,
    currentDeployments: makeQueryFilterIn({
      items: currentDeployments ?? [],
      filterKey: "deployment_id",
      byItemKey: "id",
    }),
    filteredDeployments: makeQueryFilterIn({
      items: filteredDeployments ?? [],
      filterKey: "deployment_id",
      byItemKey: "id",
    }),
  };
};

export const useCreateDeployment = spawnCreateMutationHook({
  mutationFn: createDeployment,
  notifyEntity: entity,
  analyticsEntity: apiConfigDeployments.table,
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});
export const useUpdateDeployment = (id?: string) =>
  spawnUpdateMutationHook({
    mutationFn: updateDeployment,
    notifyEntity: entity,
    analyticsEntity: apiConfigDeployments.table,
    entityQueryKey: deploymentsQueryKeys.byId(id),
    invalidationQueryKeys: [deploymentsQueryKeys.lists()],
  })();

export const useDeleteDeployment = spawnDeleteMutationHook({
  mutationFn: deleteDeployment,
  notifyEntity: entity,
  analyticsEntity: apiConfigDeployments.table,
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});
